import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { VesselsAPI, Vessel, VesselStatus, VesselCategory } from '../services/vessels.ts';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import BoatIcon from '../components/icons/BoatIcon.tsx';

const Vessels: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [sortField, setSortField] = useState<keyof Vessel>('purchaseDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | VesselStatus>(VesselStatus.IN_STOCK);
  const [categoryFilter, setCategoryFilter] = useState<'all' | VesselCategory>('all');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [vesselToDelete, setVesselToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data, isLoading, error, refetch } = useQuery<Vessel[]>(
    'vessels',
    async () => {
      const res = await VesselsAPI.list();
      return res;
    },
    { staleTime: 1000 * 60 * 5 }
  );

  const filtered =
    data?.filter(v => {
      const matchesSearch =
        v.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || v.status === statusFilter;
      const matchesCategory = categoryFilter === 'all' || (v.category || 'other') === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    }) || [];

  const sorted = [...filtered].sort((a, b) => {
    const aValue = a[sortField] as any;
    const bValue = b[sortField] as any;
    if (aValue !== undefined && bValue !== undefined) {
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const handleDelete = (id: number) => {
    setVesselToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!vesselToDelete) return;
    setIsDeleting(true);
    try {
      await VesselsAPI.delete(vesselToDelete);
      refetch();
      setIsDeleteModalOpen(false);
      setVesselToDelete(null);
    } catch (err) {
      console.error('Erro ao excluir embarcação:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setVesselToDelete(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 p-8">
        <p className="text-lg">Erro ao carregar embarcações.</p>
        <p className="text-sm mt-2">Por favor, tente novamente.</p>
      </div>
    );
  }

  const activeCount = filtered.filter(v => v.status === VesselStatus.IN_STOCK).length;
  const totalPurchaseValue = filtered.reduce((sum, v) => sum + (Number(v.purchasePrice) || 0), 0);
  const totalSellingValue = filtered.reduce((sum, v) => sum + (Number(v.sellingPrice) || 0), 0);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BoatIcon className="h-8 w-8 mr-3 text-blue-600" />
            Embarcações
          </h1>
          <p className="text-gray-600">Gerencie seu inventário de embarcações</p>
        </div>
        <button
          onClick={() => navigate('/vessels/new')}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nova Embarcação
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <BoatIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Embarcações</p>
                <p className="text-2xl font-bold text-gray-900">{filtered.length}</p>
              </div>
            </div>
            <div className="flex items-center">
              <CalendarIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Em Estoque</p>
                <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CurrencyDollarIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valor Total de Compra</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalPurchaseValue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valor Total de Venda</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalSellingValue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por marca, modelo ou registro..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="min-w-[200px]">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter((e.target.value as any) as 'all' | VesselStatus)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value={VesselStatus.IN_STOCK}>Em Estoque</option>
              <option value={VesselStatus.EXCHANGED}>Permutados</option>
              <option value={VesselStatus.SOLD}>Vendidos</option>
              <option value={"all"}>Todos os Status</option>
            </select>
          </div>
          <div className="min-w-[200px]">
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter((e.target.value as any) as 'all' | VesselCategory)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value={"all"}>Todas as Categorias</option>
              <option value={"boat"}>Barco</option>
              <option value={"yacht"}>Iate</option>
              <option value={"sailboat"}>Veleiro</option>
              <option value={"other"}>Outro</option>
            </select>
          </div>
        </div>
      </div>

      {/* Vessels Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sorted.map(vessel => (
          <div
            key={vessel.id}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {vessel.make} {vessel.model}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {vessel.year} • {vessel.color}
                    {vessel.category && (
                      <> • {({ boat: 'Barco', yacht: 'Iate', sailboat: 'Veleiro', other: 'Outro' } as Record<string, string>)[vessel.category] || 'Não informado'}</>
                    )}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(vessel.status)}`}
                >
                  {formatStatus(vessel.status)}
                </span>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Registro:</span>
                  <span className="font-semibold text-gray-900">{vessel.registrationNumber}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Valor de Compra:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(vessel.purchasePrice)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Valor de Venda:</span>
                  <span className="font-semibold text-green-600">
                    {vessel.sellingPrice ? formatCurrency(vessel.sellingPrice) : '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Data de Compra:</span>
                  <span className="font-semibold text-gray-900">{new Date(vessel.purchaseDate).toLocaleDateString('pt-BR')}</span>
                </div>
                {vessel.status === VesselStatus.SOLD && vessel.soldDate && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Data de Venda:</span>
                    <span className="font-semibold text-gray-900">{new Date(vessel.soldDate).toLocaleDateString('pt-BR')}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => navigate(`/vessels/${vessel.id}`)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  Ver
                </button>
                {vessel.assetId && vessel.status === VesselStatus.IN_STOCK && (
                  <button
                    onClick={() => navigate(`/exchanges/new?originType=vessel&originId=${vessel.assetId}`)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-purple-200 text-sm font-medium rounded-xl text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200"
                  >
                    Iniciar Permuta
                  </button>
                )}
                <button
                  onClick={() => navigate(`/edit-vessel/${vessel.id}`)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-primary-200 text-sm font-medium rounded-xl text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Editar
                </button>
                {hasPermission('assets.delete') && (
                  <button
                    onClick={() => handleDelete(vessel.id)}
                    className="px-3 py-2 border border-red-200 text-sm font-medium rounded-xl text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {sorted.length === 0 && (
        <div className="text-center py-12">
          <BoatIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma embarcação encontrada</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm ? 'Tente ajustar sua busca.' : 'Comece adicionando sua primeira embarcação.'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => navigate('/vessels/new')}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 mx-auto transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Adicionar Embarcação
            </button>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Excluir Embarcação"
        message="Tem certeza que deseja excluir esta embarcação? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Vessels;
